import { BoldText, DangerText, MuteText, SuccessText } from '@elements/Common';
import { DateFormat, DateUtility } from '@utility/date';
import {
  cloneElement,
  isValidElement,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { CommonUtility } from '@utility/common';
import { ProductRow } from '@components/CatalogElements';
import { AppTableCell } from '@components/AppTable';
import { Button, cn, Spinner } from '@skupreme/shared/ui';
import {
  ArrowDownIcon,
  FactoryIcon,
  MapPinIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import TextCopyToClipboard from '@components/TextCopyToClipboard';
import {
  ShipmentStatus,
  ShippingTypesIcons,
  StorageIntegrationTypes,
} from '@utility/constants/common';
import { DarkPopup } from '@elements/Popup';
import { Flag } from '@elements/Flag';
import { StatusTrackerTrio } from '@page-components/purchase-order/List';
import { calculateListTotalV2, ShipmentError } from '../common';
import { viewTypes } from '../ItemsCommon';
import {
  AccordionCardTableCell,
  CardSideBorderTableCell,
  CardTableRow,
} from '@components/CardTable';
import type { IShipment } from 'types/shipment/types';

const ROW = 'sk:mb-0.75 sk:text-start';

/* Same as `OriginRow` in scm/shipment-card, kept here so this row does not load that legacy file. */
function OriginNames({ origins }: { origins?: string[] }) {
  if (!origins?.length) {
    return <span />;
  }
  if (origins.length === 1) {
    return <span>{origins[0]}</span>;
  }
  return (
    <DarkPopup trigger={<span>Multiple</span>}>
      <div>
        {origins.map(name => (
          <div key={name}>{name}</div>
        ))}
      </div>
    </DarkPopup>
  );
}

function StatusLabel({ children }: { children: ReactNode }) {
  return <MuteText className="sk:inline-block sk:w-20">{children}</MuteText>;
}

type IShippingRowProps = {
  item?: IShipment;
  rowClicked: (item: any) => void;
  deleteShipment: (shipment: any) => void;
  firebaseShipmentLoading?: { [key: string]: boolean };
};

export function ShippingRow({
  item,
  rowClicked,
  deleteShipment,
  firebaseShipmentLoading,
}: IShippingRowProps) {
  const [summary, setSummary] = useState({
    cartons: 0,
    units: 0,
    retails: 0,
    supplierValue: 0,
    grossWeight: 0,
  });

  const isUnitView = item?.qtyType === viewTypes.units;

  useEffect(() => {
    if (!item) return;

    const itemTotal = calculateListTotalV2(
      item?.items,
      item?.qtyType === viewTypes.units,
    );
    setSummary(itemTotal);
  }, [item]);

  const weightValue = useMemo(() => {
    if (item?.origin?.length === 1) {
      if (
        item.destination?.address?.country === 'US' &&
        item.origin?.[0]?.address?.country === 'US'
      ) {
        if (summary.grossWeight) {
          return CommonUtility.kiloToPound(summary.grossWeight);
        }
      }
    }
    return summary.grossWeight;
  }, [summary, item]);

  const weightUnit = useMemo(() => {
    if (item?.origin?.length === 1) {
      if (
        item.destination?.address?.country === 'US' &&
        item.origin?.[0]?.address?.country === 'US'
      ) {
        return 'lb';
      }
    }
    return 'kg';
  }, [item]);

  const allItems = useMemo(() => item?.items, [item]);

  const isLoading = item?._id ? firebaseShipmentLoading?.[item._id] : false;

  // default currency to USD
  const currency = useMemo(() => item?.origin?.[0]?.currency || 'USD', [item]);

  function AmazonOriginReconciler() {
    // Hardcoding for now as this is the only origin right now
    const importedFrom = 'Amazon';
    return (
      <div className="sk:text-left">
        <b>Imported from {importedFrom}</b>
        {/* biome-ignore lint/a11y/noStaticElementInteractions: only stops the row's click; the buttons inside are the controls */}
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: keyboard presses on the buttons never reach the row */}
        <div
          className="sk:flex sk:items-center sk:gap-3 sk:pt-2"
          onClick={event => event.stopPropagation()}
        >
          <Button variant="danger" onPress={() => rowClicked(item)}>
            Enter Origin
          </Button>
          <Button
            shape="circle"
            aria-label="Delete shipment"
            onPress={() => deleteShipment(item)}
          >
            <TrashIcon size={16} aria-hidden />
          </Button>
        </div>
      </div>
    );
  }

  const isFromAmazonAndOriginBug =
    item?.isShipmentCreatedFromAmazon && !item?.otherDetails?.isReconciled;

  if (!item) {
    return <></>;
  }

  return (
    <CardTableRow
      textAlign="center"
      verticalAlign="middle"
      onClick={async () => rowClicked(item)}
      hoverColor="var(--sk-info-bg)"
    >
      <CardSideBorderTableCell
        borderColor="var(--sk-info-fg)"
        textAlign="left"
        className="sk:pl-4"
      >
        {item?.otherDetails?.shipment_error_alert ? (
          <DangerText>
            <TextCopyToClipboard text={item?.displayId || ''} />
          </DangerText>
        ) : (
          <BoldText className="sk:text-info">
            <TextCopyToClipboard text={item?.displayId || ''} />
          </BoldText>
        )}
        <div className="sk:mt-2 sk:max-w-full sk:overflow-x-auto sk:[&_.img-container]:w-1/3 sk:[&_.img-container]:max-w-20 sk:[&_img]:w-full!">
          <ProductRow
            showDetail={false}
            containerClass="sk:justify-start!"
            products={
              allItems?.map(i => ({
                p_images: {
                  display_image: i.displayImage,
                },
                p_sku: i.sku,
              })) || []
            }
          />
        </div>
        {(allItems?.length || 0) > 3 && (
          <MuteText className="sk:pl-2">
            {allItems?.length} total items
          </MuteText>
        )}
      </CardSideBorderTableCell>
      <AppTableCell data-field-value="shipment-details" verticalAlign="middle">
        <div className={cn(ROW, 'sk:flex sk:items-center')}>
          <ShipmentError
            iconProps={{ style: { marginBottom: '1px' } }}
            shipment={item?.otherDetails}
          />
        </div>
        {item.fbaId && (
          <div className={cn(ROW, 'sk:flex')}>
            <MuteText className="sk:mr-2">FBA ID</MuteText>
            <BoldText>
              <TextCopyToClipboard text={item?.fbaId} />
            </BoldText>
          </div>
        )}
        {!isUnitView && (
          <div className={ROW}>
            <MuteText>Cartons</MuteText> &nbsp;
            <BoldText>
              {CommonUtility.numberWithCommas(summary?.cartons?.toFixed())}
            </BoldText>
          </div>
        )}
        <div className={ROW}>
          <MuteText>Units</MuteText> &nbsp;
          <BoldText>
            {CommonUtility.numberWithCommas(summary.units.toFixed())}
          </BoldText>
        </div>
        {!isUnitView && (
          <div className={ROW}>
            <MuteText>Gross Weight</MuteText> &nbsp;
            <BoldText>
              {CommonUtility.numberWithCommas(weightValue.toFixed(2))}
              &nbsp;
              {weightUnit}
            </BoldText>
          </div>
        )}
        {!!item.totalPallets && (
          <div className={ROW}>
            <MuteText>Confirmed Pallets</MuteText> &nbsp;
            <BoldText>
              {CommonUtility.numberWithCommas(item.totalPallets)}
            </BoldText>
          </div>
        )}
      </AppTableCell>
      <AppTableCell data-field-value="shipment-value" verticalAlign="middle">
        <div className="sk:flex sk:flex-col sk:items-start">
          <MuteText className="sk:text-xs">Retail Value</MuteText>
          <BoldText>{CommonUtility.currencyFormat(summary.retails)}</BoldText>
          <MuteText className="sk:text-xs">Supplier Cost Value</MuteText>
          <BoldText>
            {CommonUtility.currencyFormat(summary.supplierValue, currency)}
          </BoldText>
        </div>
      </AppTableCell>
      <AppTableCell data-field-value="shipment-type" verticalAlign="middle">
        <ShipmentOriginDestinationRenderer
          item={item}
          isFromAmazonAndOriginBug={isFromAmazonAndOriginBug}
        />
      </AppTableCell>
      <AccordionCardTableCell
        data-field-value="shipment-status"
        verticalAlign="middle"
      >
        {isFromAmazonAndOriginBug ? (
          <AmazonOriginReconciler />
        ) : (
          <>
            <div className="sk:mb-3 sk:flex sk:items-center sk:gap-2">
              {isLoading && <Spinner size="sm" label="Updating shipment" />}
              {/* @ts-expect-error - TS2741: Property 'fallBackElement' is missing in type '{ showProgres... */}
              <StatusStepper showProgress={!isLoading} status={item.status} />
            </div>
            <div className={ROW}>
              <StatusLabel>Created </StatusLabel> &nbsp;
              <MuteText>
                {DateUtility.formatDate(item.createdAt, DateFormat.date)}
              </MuteText>
            </div>
            {[
              ShipmentStatus.preparing,
              ShipmentStatus.ready_for_shipment,
              ShipmentStatus.draft,
            ].includes(item.status as any) ? (
              <div className={ROW}>
                <StatusLabel>ETD </StatusLabel> &nbsp;
                <BoldText>
                  {DateUtility.formatDate(item.etd, DateFormat.date)}
                </BoldText>
              </div>
            ) : (
              <div className={ROW}>
                <StatusLabel>Departed </StatusLabel> &nbsp;
                <BoldText>
                  {DateUtility.formatDate(item.shipped, DateFormat.date)}
                </BoldText>
              </div>
            )}
            {item.status === ShipmentStatus.completed ? (
              <div className={ROW}>
                <StatusLabel>Arrived </StatusLabel> &nbsp;
                <BoldText>
                  {DateUtility.formatDate(item.arrived, DateFormat.date)}
                </BoldText>
              </div>
            ) : (
              <div className={ROW}>
                <StatusLabel>ETA </StatusLabel> &nbsp;
                <BoldText>
                  {DateUtility.formatDate(item.eta, DateFormat.date)}
                </BoldText>
              </div>
            )}
          </>
        )}
      </AccordionCardTableCell>
    </CardTableRow>
  );
}

const ELLIPSIS_ROW = cn(
  'sk:flex sk:items-center',
  'sk:[&>span]:max-w-[calc(100%-30px)] sk:[&>span]:truncate',
);

type IShipmentOriginDestinationRendererProps = {
  item: IShipment;
  isFromAmazonAndOriginBug?: boolean;
  showShippingType?: boolean;
  orgDestinationContainerClass?: string;
};

function ShipmentOriginDestinationRenderer({
  item,
  isFromAmazonAndOriginBug,
  showShippingType = true,
  orgDestinationContainerClass = '',
}: IShipmentOriginDestinationRendererProps) {
  return (
    <div className="sk:flex sk:flex-col sk:justify-center">
      {showShippingType && (
        <div className="sk:flex sk:items-center">
          <img
            width={22}
            alt=""
            className="sk:min-w-5.5"
            src={ShippingTypesIcons[item.shippingType]}
          />
          <BoldText className="sk:ml-1">{item.shippingType}</BoldText>
        </div>
      )}
      <div
        className={cn(
          'sk:mt-3 sk:max-w-[10vw] sk:min-w-28 sk:text-left sk:text-dense',
          orgDestinationContainerClass,
        )}
      >
        {!isFromAmazonAndOriginBug && (
          <>
            <div className={cn(ELLIPSIS_ROW, 'sk:mb-1')}>
              {item.originType === 'Purchase Order' ? (
                <FactoryIcon
                  className="sk:mr-2 sk:min-w-4.5"
                  size={18}
                  aria-hidden
                />
              ) : (
                <img
                  className="sk:mr-2 sk:min-w-4.5"
                  width={15}
                  alt=""
                  src={ShippingTypesIcons.SP}
                />
              )}
              <Flag name={item.origin?.[0]?.address?.country} />
              <OriginNames origins={item.origin?.map(x => x.name)} />
            </div>
            <div className="sk:mt-0.5 sk:ml-0.5">
              <div className="sk:-mb-0.5 sk:ml-1.5 sk:h-0.5 sk:w-px sk:bg-fg" />
              <ArrowDownIcon size={13} aria-hidden />
            </div>
          </>
        )}
        <div className={ELLIPSIS_ROW}>
          <MapPinIcon className="sk:min-w-4.5" size={18} aria-hidden />
          <Flag
            className="sk:ml-1 sk:min-w-4.5"
            name={item.destination?.address?.country}
          />
          {item.destination?.type !== StorageIntegrationTypes.FBA && (
            <span>{item.destination?.name}</span>
          )}
          {item.destination?.type === StorageIntegrationTypes.FBA && (
            <div className="sk:flex sk:items-center sk:justify-center sk:rounded-sm sk:bg-primary sk:p-0.75">
              <img
                className="sk:h-3"
                alt="FBA"
                src={CommonUtility.getCdnLink('marketplace/fba-icon.png')}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function StatusStepper({
  status,
  customClass = '',
  showProgress = true,
  fallBackElement,
}) {
  const stepList = ['Planning', 'Shipped', 'Completed'];
  const statusMap = [
    ['preparing'],
    ['ready_for_shipment', 'in_transit', 'shipped'],
    ['completed', 'receiving'],
  ];

  const currentIndex = useMemo(() => {
    const index = statusMap.findIndex(x => x.includes(status));
    return index;
  }, [status]);

  if (status === 'cancelled') {
    return (
      <DangerText>
        <strong>Cancelled</strong>
      </DangerText>
    );
  }
  if (currentIndex === -1 && isValidElement(fallBackElement)) {
    return <>{cloneElement(fallBackElement)}</>;
  }

  return (
    <div className={cn('sk:flex sk:items-center', customClass)}>
      {showProgress && (
        <StatusTrackerTrio color="var(--sk-success-fg)" index={currentIndex} />
      )}
      <BoldText className="sk:ml-3">
        {stepList[currentIndex] ? (
          <SuccessText>{stepList[currentIndex]}</SuccessText>
        ) : (
          <MuteText>{CommonUtility.toTitleCase(status)}</MuteText>
        )}
      </BoldText>
    </div>
  );
}
